import { useState, useEffect } from 'react';

export const useMediaPermissions = () => {
    const [permissionState, setPermissionState] = useState({
        camera: 'prompt',
        microphone: 'prompt'
    });
    const [error, setError] = useState(null);

    const checkPermissions = async () => {
        try {
            const permissions = await Promise.all([
                navigator.permissions.query({ name: 'camera' }),
                navigator.permissions.query({ name: 'microphone' })
            ]);
            
            setPermissionState({
                camera: permissions[0].state,
                microphone: permissions[1].state
            });

            return {
                camera: permissions[0].state,
                microphone: permissions[1].state
            };
        } catch (error) {
            console.error('Error checking permissions:', error);
            setError('Failed to check permissions');
            return null;
        }
    };

    const requestPermissions = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: true, 
                audio: true 
            });
            
            // Stop the stream immediately as we just needed it for permission
            stream.getTracks().forEach(track => track.stop());
            
            // Update permission states
            const newState = await checkPermissions();
            return true;
        } catch (error) {
            console.error('Error requesting permissions:', error);
            
            // Update permission states based on error
            if (error.name === 'NotAllowedError') {
                setPermissionState({
                    camera: 'denied',
                    microphone: 'denied'
                });
                setError({
                    type: error.constraint || 'camera',
                    message: 'Permission denied. Please enable access in your browser settings.'
                });
            } else {
                setError({
                    type: 'unknown',
                    message: 'An error occurred while requesting permissions.'
                });
            }
            return false;
        }
    };

    const requestSinglePermission = async (type) => {
        try {
            const constraints = {
                [type]: true
            };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            stream.getTracks().forEach(track => track.stop());
            
            // Update permission states
            await checkPermissions();
            return true;
        } catch (error) {
            console.error(`Error requesting ${type} permission:`, error);
            if (error.name === 'NotAllowedError') {
                setPermissionState(prev => ({
                    ...prev,
                    [type]: 'denied'
                }));
                setError({
                    type,
                    message: `${type.charAt(0).toUpperCase() + type.slice(1)} permission denied. Please enable access in your browser settings.`
                });
            }
            return false;
        }
    };

    useEffect(() => {
        checkPermissions();
    }, []);

    return {
        permissionState,
        error,
        checkPermissions,
        requestPermissions,
        requestSinglePermission
    };
}; 